import { useRef, useState } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { MdOutlineKeyboardArrowDown, MdOutlineLooksOne, MdOutlineLooksTwo } from 'react-icons/md';
import { HrSystemConflictCard } from '@lp/features/Scheduler/components';
import { OMSShiftCellDropdown } from '@lp/features/Scheduler/SchedulerOMS/components';
import { omsShiftOptionTypeNames } from '@lp/features/Scheduler/SchedulerOMS/constants';
import {
  getOMSShiftSetData,
  getShiftOptionFromShiftSlot,
  getShiftOptionIconColor,
  isDualSlotOptionType,
} from '@lp/features/Scheduler/SchedulerOMS/utils';
import {
  OMSResolveHrSystemConflict,
  OMSResourceOMSShiftTypeHrSystemConflict,
  OMSShift,
  OMSShiftSelectPayload,
  OMSShiftSlot,
  ScheduleHrSystemConflictType,
} from '@/api/apis/resources/schedule';
import { shiftTypeNames } from '@/constants/schedule.constants';

type OMSHrSystemConflictCardProps = {
  conflict: OMSResourceOMSShiftTypeHrSystemConflict;
  onConflictResolve: (resolveData: OMSResolveHrSystemConflict) => void;
};

export const OMSHrSystemConflictCard = ({ conflict, onConflictResolve }: OMSHrSystemConflictCardProps) => {
  const { conflictId, type, resource, hrSystemShiftType } = conflict;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [shiftPayload, setShiftPayload] = useState<OMSShiftSelectPayload>();

  const shiftOption1 = getShiftOptionFromShiftSlot(shiftPayload?.slot1 as OMSShiftSlot);
  const shiftOption2 = getShiftOptionFromShiftSlot(shiftPayload?.slot2 as OMSShiftSlot);
  const shiftOption = shiftOption1 || shiftOption2;
  const textColor = shiftOption ? getShiftOptionIconColor(shiftOption) : 'text-base-25';
  const isRemoved = type === ScheduleHrSystemConflictType.HrSystemShiftTypeRemoved;
  const isDualSlot = isDualSlotOptionType(shiftOption);

  const onShiftChange = (payload?: OMSShiftSelectPayload) => {
    setShiftPayload((current) => {
      if (!payload) return undefined;

      return {
        ...current,
        ...payload,
        slot1: payload.slot1 === undefined ? current?.slot1 : payload.slot1,
        slot2: payload.slot2 === undefined ? current?.slot2 : payload.slot2,
      };
    });
  };

  const onApply = () => {
    const hrPayload: OMSShiftSelectPayload = {
      slotsPerDay: 1,
      slot1: { shiftType: hrSystemShiftType },
    };
    const payload = isRemoved ? shiftPayload : hrPayload;
    if (payload) {
      const setShiftData = getOMSShiftSetData(payload, resource);
      onConflictResolve({ conflictId, setShiftData });
    }
  };

  const getSlotsText = () => {
    const textColor1 = shiftOption1 ? getShiftOptionIconColor(shiftOption1) : 'text-base-25';
    const textColor2 = shiftOption2 ? getShiftOptionIconColor(shiftOption2) : 'text-base-25';

    return (
      <div className="flex flex-col gap-0.5 items-start">
        {shiftOption1 && (
          <span className={cn('flex items-center gap-1', textColor1)}>
            {omsShiftOptionTypeNames[shiftOption1]}
            {isDualSlot && <MdOutlineLooksOne size={18} />}
          </span>
        )}
        {shiftOption2 && (
          <span className={cn('flex items-center gap-1', textColor2)}>
            {omsShiftOptionTypeNames[shiftOption2]}
            <MdOutlineLooksTwo size={18} />
          </span>
        )}
      </div>
    );
  };

  return (
    <HrSystemConflictCard conflict={conflict} isApplyDisabled={isRemoved && !shiftPayload} onApply={onApply}>
      {isRemoved ? (
        <div className="flex w-full">
          <button
            ref={triggerRef}
            className={cn('flex items-center text-m-r hover:underline', textColor)}
            onClick={toggleIsOpen}
          >
            {shiftOption ? getSlotsText() : 'No shift'}
            <MdOutlineKeyboardArrowDown size={18} className={cn({ 'rotate-180': isOpen })} />
          </button>
          {isOpen && (
            <OMSShiftCellDropdown
              triggerRef={triggerRef}
              title="Pick Shift"
              isPlacementBottom
              isOpen={isOpen}
              onOpenChange={toggleIsOpen}
              shiftData={shiftPayload as unknown as OMSShift}
              onShiftChange={onShiftChange}
              canReset
            />
          )}
        </div>
      ) : (
        <span>{hrSystemShiftType ? shiftTypeNames[hrSystemShiftType] : 'Unknown'}</span>
      )}
    </HrSystemConflictCard>
  );
};
